// Restock Recommendations — ranked list of SKUs needing an order,
// composed on top of Inventory Health Monitor's stock-out risk output.
// Entry from IHM's Overview (?sku= param pre-selects the drill).
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import * as api from './services/restockApi';
import styles from './RestockRecommendations.module.css';

const SEASON_LABEL = {
  in_season: 'In season', off_season: 'Off season', evergreen: 'Evergreen', unknown: 'No seasonal pattern',
};

const INR = (paise) => {
  if (paise == null) return '—';
  const v = paise / 100;
  if (v >= 10_000_000) return `₹${(v / 10_000_000).toFixed(2)} Cr`;
  if (v >= 100_000) return `₹${(v / 100_000).toFixed(2)} L`;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
};

const statusChipCls = (s) => {
  if (s === 'already_late') return styles.statusLate;
  if (s === 'order_now') return styles.statusNow;
  if (s === 'scheduled') return styles.statusScheduled;
  return styles.statusNeutral;
};

const Tile = ({ label, value, hint, accent }) => (
  <div className={`${styles.tile} ${accent ? styles[`tile_${accent}`] : ''}`}>
    <div className={styles.tileLabel}>{label}</div>
    <div className={styles.tileValue}>{value ?? '—'}</div>
    {hint && <div className={styles.tileHint}>{hint}</div>}
  </div>
);

const Assumption = ({ label, value, isDefault }) => (
  <span className={styles.assumption}>
    {label}: <b>{value}</b>
    {isDefault && <span className={styles.pillMuted}>assumed</span>}
  </span>
);

// ── Drill drawer ────────────────────────────────────────────────────────────

const DetailDrawer = ({ sku, onClose, onSaved }) => {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [override, setOverride] = useState({ moq: '', pack_size: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const body = await api.getRecommendationDetail(authCtx.token, sku);
      setState({ loading: false, data: body.data, error: null });
      if (body.data?.found) {
        setOverride({
          moq: body.data.moq || '',
          pack_size: body.data.pack_size || '',
        });
      }
    } catch (e) {
      setState({ loading: false, data: null, error: e.message });
    }
  }, [authCtx.token, sku]);
  useEffect(() => { load(); }, [load]);

  const savePack = async () => {
    setSaving(true);
    try {
      await api.setSkuPack(
        authCtx.token, sku,
        Number(override.moq) || null,
        Number(override.pack_size) || null);
      await load();
      onSaved?.();
    } catch (e) {
      setState((s) => ({ ...s, error: e.message }));
    } finally {
      setSaving(false);
    }
  };

  const { loading, data, error } = state;
  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.drawerHeader}>
          <div>
            <h3>SKU {sku}</h3>
            {data?.found && (
              <div className={styles.subtle}>
                Rank #{data.rank} · {data.state} ·{' '}
                <span className={`${styles.chip} ${statusChipCls(data.status)}`}>
                  {data.status.replace('_', ' ')}
                </span>
                {data.asin && (
                  <>{' · '}
                    <button className={styles.linkBtn}
                            onClick={() => navigate(`/dashboard/seller-intelligence/inventory-health?tab=stock-out-risk&asin=${data.asin}`)}>
                      View in Inventory Health →</button></>
                )}
              </div>
            )}
          </div>
          <button onClick={onClose} className={styles.close}>✕</button>
        </div>
        <div className={styles.drawerBody}>
          {loading && <div className={styles.muted}>Loading…</div>}
          {error && <div className={styles.error}>{error}</div>}
          {data && !data.found && <div className={styles.muted}>{data.message}</div>}
          {data?.found && (
            <>
              <div className={styles.tileGrid}>
                <Tile label="Recommended qty" value={data.recommended_qty}
                      accent={data.status === 'order_now' || data.status === 'already_late' ? 'critical' : null} />
                <Tile label="Order by" value={data.order_by_date || '—'}
                      hint={data.days_until_order_by != null ?
                            `${data.days_until_order_by} days` : ''} />
                <Tile label="Horizon" value={`${data.horizon_days}d`}
                      hint={`lead ${data.assumptions.lead_time_days}d + review ${data.assumptions.review_interval_days}d`} />
                <Tile label="Confidence" value={data.confidence} />
                <Tile label="Order cost" value={INR(data.order_cost_minor)}
                      hint={data.unit_cost_minor != null
                        ? `${INR(data.unit_cost_minor)}/unit from Profit & Margin`
                        : 'no cost entered in Profit & Margin'} />
              </div>

              <h4 className={styles.sectionTitle}>Quantity breakdown</h4>
              <table className={styles.breakdown}>
                <tbody>
                  <tr>
                    <td>Expected demand across horizon</td>
                    <td className={styles.right}>+{data.expected_demand_in_horizon}</td>
                  </tr>
                  <tr>
                    <td>Safety buffer ({data.assumptions.safety_buffer_days}d @ {data.assumptions.service_level_pct}%)</td>
                    <td className={styles.right}>+{data.safety_buffer_units}</td>
                  </tr>
                  <tr>
                    <td>Fulfillable stock (subtracted)</td>
                    <td className={styles.right}>−{data.fulfillable_deducted}</td>
                  </tr>
                  <tr>
                    <td>
                      Qualifying inbound (subtracted)
                      {data.fc_transfer_deducted > 0 && ` — incl. ${data.fc_transfer_deducted} moving between FCs`}
                      {data.own_warehouse_deducted > 0 && ` — incl. ${data.own_warehouse_deducted} in your own warehouse`}
                    </td>
                    <td className={styles.right}>−{data.qualifying_inbound_deducted}</td>
                  </tr>
                  <tr className={styles.totalRow}>
                    <td>= Recommended (after MOQ×{data.moq} & pack×{data.pack_size} rounding)</td>
                    <td className={styles.right}>{data.recommended_qty}</td>
                  </tr>
                </tbody>
              </table>

              {(data.moq_conflict || data.capped_for_excess) && (
                <div className={`${styles.banner} ${styles.bannerWarn}`}>
                  {data.moq_conflict && <>MOQ raises quantity above healthy cover. </>}
                  {data.capped_for_excess && <>Quantity capped to prevent excess.{' '}
                    <button className={styles.linkBtn}
                            onClick={() => navigate('/dashboard/seller-intelligence/inventory-performance?tab=excess')}>
                      See excess →</button> </>}
                </div>
              )}

              <h4 className={styles.sectionTitle}>Demand by month</h4>
              <div className={styles.muted}>
                {data.assumptions.demand_source === 'seasonal_performance'
                  ? `Seasonal forecast (${SEASON_LABEL[data.assumptions.season] || data.assumptions.season}): trend × the month's seasonal index × event lift.`
                  : 'Flat recent sales rate — this product has no seasonal pattern to forecast from.'}
                {data.season_sensitive && ' Long horizon on a seasonal product: the quantity depends heavily on how the season lands.'}
              </div>
              <table className={styles.breakdown}>
                <tbody>
                  {(data.demand_by_month || []).map((m) => (
                    <tr key={m.month}>
                      <td>
                        {m.month} · {m.days}d
                        {m.seasonal_index != null && ` · index ${m.seasonal_index}`}
                        {m.events?.length > 0 && ` · ${m.events.join(', ')}${m.event_lift ? ` (×${m.event_lift})` : ''}`}
                      </td>
                      <td className={styles.right}>{m.units}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h4 className={styles.sectionTitle}>Timing walk-forward</h4>
              <div className={styles.projection}>
                {(data.projection_full_90d || []).filter((_, i) => i % 5 === 0).map((p) => (
                  <div key={p.day} className={styles.projDot}
                       style={{
                         height: `${Math.max(4, Math.min(40, p.on_hand_after / 3))}px`,
                         background: p.on_hand_after <= 0 ? '#ef4444' :
                                     p.on_hand_after < data.safety_buffer_units ? '#f59e0b' : '#10b981',
                       }}
                       title={`Day ${p.day}: ${p.on_hand_after} on hand`} />
                ))}
              </div>
              <div className={styles.subtle}>
                90-day projection. Red = zero-stock; amber = below safety buffer;
                green = comfortable.
              </div>

              <h4 className={styles.sectionTitle}>Override MOQ / pack size (#25)</h4>
              <div className={styles.overrideForm}>
                <label>
                  MOQ
                  <input type="number" min={1} value={override.moq}
                         onChange={(e) => setOverride({ ...override, moq: e.target.value })} />
                </label>
                <label>
                  Pack size
                  <input type="number" min={1} value={override.pack_size}
                         onChange={(e) => setOverride({ ...override, pack_size: e.target.value })} />
                </label>
                <button onClick={savePack} disabled={saving} className={styles.btnPrimary}>
                  {saving ? 'Saving…' : 'Save + recompute'}
                </button>
              </div>

              <div className={styles.assumptions}>
                Assumptions:{' '}
                <Assumption label="Lead time" value={`${data.assumptions.lead_time_days}d`}
                            isDefault />
                <Assumption label="Buffer" value={`${data.assumptions.safety_buffer_days}d`}
                            isDefault />
                <Assumption label="Service level" value={`${data.assumptions.service_level_pct}%`}
                            isDefault />
                <Assumption label="Demand"
                            value={data.assumptions.demand_source === 'seasonal_performance'
                              ? 'seasonal forecast' : 'flat rate'}
                            isDefault={data.assumptions.demand_source !== 'seasonal_performance'} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────

const RestockRecommendations = () => {
  const authCtx = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const preSelectSku = searchParams.get('sku');
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [openSku, setOpenSku] = useState(preSelectSku);

  const load = useCallback(async () => {
    if (!authCtx?.token) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const body = await api.getRecommendations(authCtx.token);
      setState({ loading: false, data: body.data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: e.message });
    }
  }, [authCtx?.token]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // #45 IHM pre-selected the SKU on entry — open the drill automatically.
    if (preSelectSku && !openSku) setOpenSku(preSelectSku);
  }, [preSelectSku, openSku]);

  const closeDrill = () => {
    setOpenSku(null);
    if (searchParams.get('sku')) {
      const next = new URLSearchParams(searchParams);
      next.delete('sku');
      setSearchParams(next);
    }
  };

  const { loading, data, error } = state;
  if (loading && !data) return <div className={styles.page}>Loading…</div>;
  if (error) return <div className={styles.page}><div className={styles.error}>{error}</div></div>;
  if (!data) return null;

  const { rows, total_recommendations, excluded_counts, assumptions,
          seasonal_available, everything_comfortable } = data;

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>Restock Recommendations</h2>
          <p className={styles.pageSubtitle}>
            One ranked list of SKUs needing an order — urgency first, then
            what's at stake. Consumes Inventory Health's stock-out risk
            model directly, no second demand model.
          </p>
        </div>
        <button
          style={{ padding: '8px 16px', border: '1px solid #d1d5db',
                   borderRadius: 4, background: '#fff', cursor: 'pointer' }}
          onClick={load}>
          ↻ Refresh
        </button>
      </div>

      {!seasonal_available && (
        <div className={`${styles.banner} ${styles.bannerInfo}`}>
          Seasons haven't been worked out for this account yet, so every product is
          forecast at its recent sales rate. Seasonal products may be under- or over-ordered.
        </div>
      )}
      {everything_comfortable && (
        <div className={`${styles.banner} ${styles.bannerOk}`}>
          Everything comfortable — no restock needed right now.
          {excluded_counts.healthy > 0 && ` ${excluded_counts.healthy} SKU(s) already above the order-up-to level.`}
        </div>
      )}

      <div className={styles.tileGrid}>
        <Tile label="Recommendations" value={total_recommendations} accent="warn" />
        <Tile label="Excluded — dormant" value={excluded_counts.dormant || 0}
              hint="Zero velocity with stock" />
        <Tile label="Excluded — unmeasured" value={excluded_counts.unmeasured || 0}
              hint="Not enough history" />
        <Tile label="No order needed" value={excluded_counts.not_needed || 0}
              hint="Above order-up-to level" />
      </div>

      <div className={styles.assumptions}>
        <Assumption label="Lead time (FBA)"
                    value={`${assumptions.lead_time_days_fba}d`}
                    isDefault={assumptions.is_default.lead_time_days_fba} />
        <Assumption label="Review interval"
                    value={`${assumptions.review_interval_days}d`}
                    isDefault={assumptions.is_default.ordering_window_days} />
        <Assumption label="Safety buffer"
                    value={`${assumptions.safety_buffer_days}d`}
                    isDefault={assumptions.is_default.safety_buffer_days} />
        <Assumption label="Service level"
                    value={`${assumptions.service_level_pct}%`}
                    isDefault={assumptions.is_default.service_level_pct} />
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>SKU</th>
              <th>ASIN</th>
              <th>Status</th>
              <th>Order by</th>
              <th>Recommended qty</th>
              <th>Cover after</th>
              <th>Trend</th>
              <th>Stakes</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.sku} onClick={() => setOpenSku(r.sku)}
                  className={styles.clickable}>
                <td>{r.rank}</td>
                <td className={styles.mono}>{r.sku}</td>
                <td className={styles.mono}>{r.asin || '—'}</td>
                <td>
                  <span className={`${styles.chip} ${statusChipCls(r.status)}`}>
                    {r.status.replace('_', ' ')}
                  </span>
                </td>
                <td>
                  {r.order_by_date || '—'}
                  {r.days_until_order_by != null && (
                    <div className={styles.subtle}>
                      {r.days_until_order_by <= 0 ? 'now' : `${r.days_until_order_by}d`}
                    </div>
                  )}
                </td>
                <td className={styles.valueBold}>
                  {r.recommended_qty}
                  {r.capped_for_excess && (
                    <div className={styles.subtle}>capped</div>
                  )}
                  {r.moq_conflict && (
                    <div className={styles.subtle}>MOQ conflict</div>
                  )}
                </td>
                <td>
                  {r.cover_after_order_days != null ? `${r.cover_after_order_days}d` : '—'}
                </td>
                <td className={styles.trend}>
                  {r.trend === 'up' && '↑'} {r.trend === 'down' && '↓'}
                  {r.trend === 'flat' && '→'} {r.trend === 'unknown' && '?'}
                </td>
                <td>
                  {r.stakes_minor > 0
                    ? INR(r.stakes_minor)
                    : <span className={styles.subtle}>
                        {r.units_at_risk} at-risk units
                      </span>}
                </td>
                <td>›</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.footNote}>
        #51 No automatic ordering, PO creation, shipment or supplier contact
        happens from this page. Every recommendation is a suggestion only.
      </div>

      {openSku && (
        <DetailDrawer sku={openSku} onClose={closeDrill} onSaved={load} />
      )}
    </div>
  );
};

export default RestockRecommendations;
